import { AnimatePresence, motion } from 'framer-motion';
import { memo, useEffect, useRef, useState } from 'react';
import { Dialog, DialogDescription, DialogTitle } from '~/components/ui/LegacyDialog';
import * as Tooltip from '~/components/ui/Tooltip';
import type { FileAttachment, SimpleAttachment } from '~/types/chat';
import { cubicEasingFn } from '~/utils/easings';
import { renderLogger } from '~/utils/logger';

interface AttachmentData {
  url: string;
  extension?: string;
  name?: string;
  uploading: boolean;
  isImage: boolean;
}

interface AttachmentProps {
  attachment: FileAttachment | SimpleAttachment;
  allowRemove?: boolean;
  showWarning?: boolean;
  onRemove?: (attachment: FileAttachment) => void;
}

export const Attachment = memo(
  ({ attachment, allowRemove = false, showWarning = false, onRemove }: AttachmentProps) => {
    renderLogger.trace('Attachment');

    const [{ url, name, uploading, isImage, extension }, setAttachmentData] = useState<AttachmentData>({
      url: '',
      uploading: false,
      isImage: false,
    });

    const [showDialog, setShowDialog] = useState(false);
    const [hasWarning, setHasWarning] = useState(false);

    const fileRef = useRef<File | null>(null);
    const objectUrlRef = useRef<string | null>(null);
    const imageRef = useRef<HTMLImageElement | null>(null);

    useEffect(() => {
      return () => {
        if (objectUrlRef.current) {
          URL.revokeObjectURL(objectUrlRef.current);
          objectUrlRef.current = null;
          fileRef.current = null;
        }
      };
    }, []);

    useEffect(() => {
      if (isFileAttachment(attachment)) {
        const file = attachment.file;

        if (file !== fileRef.current) {
          // the file has changed and we must manually release the object
          if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
          }

          const objectUrl = URL.createObjectURL(file);

          objectUrlRef.current = objectUrl;
          fileRef.current = file;

          setAttachmentData({
            url: objectUrl,
            name: file.name,
            uploading: attachment.uploading,
            isImage: isImageContentType(attachment.file.type),
            extension: getFileExtension(attachment.file.name),
          });
        } else {
          // if the file hasn't changed, just update the uploading status
          setAttachmentData((prev) => {
            return { ...prev, uploading: attachment.uploading };
          });
        }
      } else {
        setAttachmentData((prevAttachment) => {
          if (prevAttachment.url === attachment.url) {
            return prevAttachment;
          }

          return {
            ...attachment,
            uploading: false,
            isImage: isImageContentType(attachment.contentType),
            extension: getFileExtension(attachment.name),
          };
        });
      }
    }, [attachment]);

    useEffect(() => {
      if (!uploading && isImage) {
        const img = new Image();

        img.onload = () => {
          imageRef.current = img;

          // we show a warning if the image is smaller than 100x100 pixels
          setHasWarning(img.width * img.height < 10_000);
        };

        img.src = url;
      }
    }, [url, uploading, isImage]);

    return (
      <Tooltip.Provider>
        <div className="relative">
          <div className="relative flex rounded-lg border border-bolt-elements-borderColor overflow-hidden">
            {uploading ? (
              <div className="h-20 w-20" />
            ) : (
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger asChild>
                  <button
                    className="h-20 w-20 bg-transparent outline-none"
                    disabled={!isImage}
                    onClick={() => setShowDialog(!showDialog)}
                  >
                    {isImage ? (
                      <img src={url} alt={name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center gap-1.5 text-xs overflow-hidden bg-bolt-elements-background-depth-2">
                        {extension ? (
                          <div className="uppercase bg-alpha-accent-20 dark:bg-alpha-accent-20 rounded-full px-2 py-0.5 text-xs text-accent-600">
                            {extension}
                          </div>
                        ) : (
                          <div className="i-ph:file-duotone text-xl" />
                        )}
                        <div className="truncate max-w-[80%]">{name}</div>
                      </div>
                    )}
                  </button>
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content side="bottom" align="center" sideOffset={6} maxWidth={180}>
                    {name}
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            )}

            <AnimatePresence>
              {uploading && (
                <motion.div
                  className="absolute flex items-center justify-center inset-0 bg-bolt-elements-prompt-attachments-backgroundLoading text-bolt-elements-textPrimary"
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2, ease: cubicEasingFn }}
                >
                  <div className="i-svg-spinners:90-ring-with-bg text-lg" />
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {allowRemove && (
            <button
              onClick={() => {
                if (isFileAttachment(attachment)) {
                  onRemove?.(attachment);
                }
              }}
              className="absolute top-0 right-0 -translate-y-1/2 translate-x-1/2 rounded-full w-[18px] h-[18px] flex items-center justify-center z-1 bg-bolt-elements-prompt-attachments-button-background hover:bg-bolt-elements-prompt-attachments-button-backgroundHover border border-bolt-elements-borderColor text-bolt-elements-prompt-attachments-button-text hover:text-bolt-elements-prompt-attachments-button-textHover"
            >
              <div className="i-ph:x scale-70" />
            </button>
          )}

          {showWarning && isImage && hasWarning && (
            <div className="absolute flex items-center justify-center -right-[9px] -bottom-[8px] rounded-full bg-bolt-elements-background-depth-1">
              <Tooltip.Root delayDuration={0}>
                <Tooltip.Trigger asChild>
                  <div className="i-ph:warning-circle-fill text-orange-500 text-xl" />
                </Tooltip.Trigger>
                <Tooltip.Portal>
                  <Tooltip.Content
                    side="bottom"
                    align="start"
                    sideOffset={4}
                    alignOffset={-16}
                    maxWidth={186}
                    truncate={false}
                  >
                    Attach an image with a higher resolution for better results
                  </Tooltip.Content>
                </Tooltip.Portal>
              </Tooltip.Root>
            </div>
          )}

          <Dialog
            open={showDialog}
            showCloseButton={false}
            contentClassName="flex flex-col items-center"
            unstyled
            width="auto"
            maxWidth="100%"
            maxHeight="100%"
            onClose={() => setShowDialog(false)}
          >
            <DialogDescription
              className="max-h-[75vh] max-w-[80vw] rounded-lg overflow-hidden"
              unstyled
              style={
                !imageRef.current
                  ? undefined
                  : {
                      aspectRatio: imageRef.current.width / imageRef.current.height,
                    }
              }
            >
              <img className="h-full w-full object-container" src={url} alt={name} />
            </DialogDescription>

            <DialogTitle className="text-white text-sm mt-2.5 text-center" unstyled>
              {name}
            </DialogTitle>
          </Dialog>
        </div>
      </Tooltip.Provider>
    );
  },
);

function isFileAttachment(attachment: FileAttachment | SimpleAttachment): attachment is FileAttachment {
  return 'file' in attachment;
}

function getFileExtension(fileName: string | undefined) {
  const parts = fileName?.split('.');

  if (!parts || parts.length === 1) {
    return undefined;
  }

  return parts.at(-1);
}

function isImageContentType(contentType: string | undefined) {
  return contentType?.startsWith('image/') ?? false;
}
